import React from 'react';
import styled, { keyframes } from 'styled-components';

// Paleta categórica dos gráficos: verde da marca primeiro, depois tons que se
// distinguem entre si e funcionam no claro e no escuro.
const LIGHT = ['#2f6f5e', '#3b7dc4', '#d08a2e', '#8a5cc2', '#c2504a', '#3f9c9c', '#7a8a2e'];
const DARK = ['#5fb595', '#6aa6e6', '#e8a957', '#b18ee6', '#e57c76', '#6cc6c6', '#a9b85a'];

export const seriesColor = (mode: string | undefined, i: number) =>
  (mode === 'dark' ? DARK : LIGHT)[i % LIGHT.length];

/* Cartão de todo componente gerado: mesma moldura, título e rodapé de fonte. */
export const Card = styled.figure`
  margin: 14px 0;
  padding: 14px 16px 12px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)')};
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.015)')};
  font-family: 'Sora', 'Lexend', sans-serif;
  max-width: 100%;
  overflow: hidden;
`;

export const Title = styled.figcaption`
  font-weight: 600;
  font-size: 0.95rem;
  line-height: 1.4;
  margin-bottom: 10px;
`;

export const Source = styled.div`
  margin-top: 10px;
  font-size: 0.74rem;
  opacity: 0.6;
`;

export const Muted = styled.span`
  color: ${({ theme }) => theme.text_secondary || theme.font_color};
  opacity: 0.75;
`;

export function CardShell({ title, source, children, label }: {
  title?: string; source?: string; children: React.ReactNode; label: string;
}) {
  return (
    <Card aria-label={title || label} data-genui={label}>
      {title && <Title>{title}</Title>}
      {children}
      {source && <Source>Fonte: {source}</Source>}
    </Card>
  );
}

const sweep = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

/* Enquanto o bloco ainda chega no stream. */
export const Skeleton = styled.div`
  height: 120px;
  border-radius: 12px;
  margin: 14px 0;
  background: linear-gradient(90deg,
    ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)')} 30%,
    ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.07)')} 50%,
    ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)')} 70%);
  background-size: 200% 100%;
  animation: ${sweep} 1.6s linear infinite;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.82rem;
  color: ${({ theme }) => theme.text_secondary || theme.font_color};
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

/** Número no formato brasileiro, sem casas desnecessárias. */
export const fmt = (n: number, digits?: number) => {
  if (!Number.isFinite(n)) return '—';
  const d = digits ?? (Math.abs(n) >= 100 ? 0 : Math.abs(n) >= 10 ? 1 : 2);
  return n.toLocaleString('pt-BR', { maximumFractionDigits: d, minimumFractionDigits: 0 });
};

export const asText = (v: unknown) => (v === null || v === undefined ? '' : String(v));
export const asNumber = (v: unknown) => {
  if (typeof v === 'number') return v;
  const s = String(v ?? '').trim();
  // "1.234,5" (pt-BR) ou "1234.5": com vírgula, o ponto é separador de milhar.
  const n = parseFloat(s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s);
  return Number.isFinite(n) ? n : NaN;
};
